import {
  ChevronLeftRounded,
  ChevronRightRounded,
  Female as FemaleIcon,
} from "@mui/icons-material";
import { Box, IconButton, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { activityApi } from "apis";
import { EPath } from "constants/path";
import {
  addDays,
  addMonths,
  endOfMonth,
  format,
  isSameMonth,
  startOfMonth,
  subMonths,
} from "date-fns";
import { isEmpty, isNil } from "lodash";
import React, { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";

import DashboardLayout from "../components/layouts/DashboardLayout";
import {
  MetricValueResponseDto,
  MetricValuesPaginationRequestDtoMetricCodeEnum,
  MetricValuesPaginationRequestDtoOrderByEnum,
} from "../libs/cellera-sdk";
import PatientDetailPageContainer from "components/features/patient/PatientDetailPageContainer";

const DAY_LABELS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const PERIOD_FLOW_VALUES = new Set(["light", "medium", "heavy"]);
const SPOTTING_FLOW_VALUES = new Set(["unspecified"]);
const RECORD_LIMIT = 1000;

interface CalendarDay {
  date: Date;
  inCurrentMonth: boolean;
  isPeriod: boolean;
  isSpotting: boolean;
}

const getDateKey = (date: Date) => format(date, "yyyy-MM-dd");

const buildMonthGrid = (
  month: Date,
  periodDateKeys: Set<string>,
  spottingDateKeys: Set<string>,
): CalendarDay[] => {
  const monthStart = startOfMonth(month);
  const monthEnd = endOfMonth(month);
  const gridStart = addDays(monthStart, -monthStart.getDay());
  const trailingDays = 6 - monthEnd.getDay();
  const totalDays = monthEnd.getDate() + monthStart.getDay() + trailingDays;

  return Array.from({ length: totalDays }, (_, index) => {
    const date = addDays(gridStart, index);
    const dateKey = getDateKey(date);

    return {
      date,
      inCurrentMonth: isSameMonth(date, monthStart),
      isPeriod: periodDateKeys.has(dateKey),
      isSpotting: spottingDateKeys.has(dateKey),
    };
  });
};

const ReproductiveHealthPage: React.FC = () => {
  const { patientId } = useParams<{ patientId: string }>();
  const navigate = useNavigate();
  const [visibleMonth, setVisibleMonth] = useState<Date>(
    startOfMonth(subMonths(new Date(), 1)),
  );

  const handleBack = () => {
    navigate(-1);
  };

  const {
    data = [],
    isLoading,
    refetch,
  } = useQuery<MetricValueResponseDto[]>({
    queryKey: ["reproductive-health-page", patientId],
    queryFn: async () => {
      if (isNil(patientId) || isEmpty(patientId)) {
        return [];
      }

      const response = await activityApi.getMetricValuesPaginatedByPatientId({
        patientId,
        metricValuesPaginationRequestDto: {
          page: 1,
          pageSize: RECORD_LIMIT,
          metricCode:
            MetricValuesPaginationRequestDtoMetricCodeEnum.Menstruation,
          orderBy: MetricValuesPaginationRequestDtoOrderByEnum.Desc,
        },
      });

      return response.data;
    },
    enabled: !isNil(patientId) && !isEmpty(patientId),
    retry: 2,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const { periodDateKeys, spottingDateKeys } = useMemo(() => {
    const periods = new Set<string>();
    const spotting = new Set<string>();

    data.forEach((record) => {
      const normalizedValue = record.valueText?.toLowerCase().trim() ?? "";
      const recordDate = new Date(record.startDate);

      if (Number.isNaN(recordDate.getTime())) {
        return;
      }

      const recordDateKey = getDateKey(recordDate);

      if (PERIOD_FLOW_VALUES.has(normalizedValue)) {
        periods.add(recordDateKey);
      }

      if (SPOTTING_FLOW_VALUES.has(normalizedValue)) {
        spotting.add(recordDateKey);
      }
    });

    return {
      periodDateKeys: periods,
      spottingDateKeys: spotting,
    };
  }, [data]);

  const monthsToRender = useMemo(
    () => [visibleMonth, addMonths(visibleMonth, 1)],
    [visibleMonth],
  );

  const calendarMonths = useMemo(
    () =>
      monthsToRender.map((month) => ({
        month,
        days: buildMonthGrid(month, periodDateKeys, spottingDateKeys),
      })),
    [monthsToRender, periodDateKeys, spottingDateKeys],
  );

  const hasCalendarData = periodDateKeys.size > 0 || spottingDateKeys.size > 0;
  const isAtMaxMonth = isSameMonth(addMonths(visibleMonth, 1), new Date());

  return (
    <DashboardLayout title="Reproductive Health">
      <PatientDetailPageContainer
        breadcrumbs={[
          {
            label: "Patient Management",
            href: EPath.PATIENT_MANAGEMENT,
            order: 0,
          },
        ]}
        lastBreadcrumbItem="Reproductive Health"
        title="Reproductive Health"
        icon={<FemaleIcon />}
        onBack={handleBack}
        onRefresh={refetch}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2.5,
          }}
        >
          <Box
            sx={{
              border: "1px solid rgba(38, 27, 34, 0.06)",
              borderRadius: "26px",
              backgroundColor: "#FFFFFF",
              boxShadow: "0 8px 22px rgba(40, 35, 41, 0.08)",
              px: { xs: 2, md: 3.25 },
              py: { xs: 2, md: 2.85 },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                mb: { xs: 1.75, md: 2.6 },
              }}
            >
              <IconButton
                aria-label="Previous months"
                onClick={() =>
                  setVisibleMonth((currentMonth) =>
                    startOfMonth(subMonths(currentMonth, 1)),
                  )
                }
                sx={{
                  color: "#2A252B",
                  width: 42,
                  height: 42,
                }}
              >
                <ChevronLeftRounded />
              </IconButton>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
                  gap: { xs: 2.75, md: 4 },
                  flex: 1,
                }}
              >
                {calendarMonths.map(({ month }) => (
                  <Typography
                    key={month.toISOString()}
                    variant="h5"
                    sx={{
                      textAlign: "center",
                      fontSize: { xs: "1.75rem", md: "2.15rem" },
                      fontWeight: 700,
                      color: "#2B272D",
                      letterSpacing: "-0.02em",
                    }}
                  >
                    {format(month, "MMMM yyyy")}
                  </Typography>
                ))}
              </Box>

              <IconButton
                aria-label="Next months"
                disabled={isAtMaxMonth}
                onClick={() =>
                  setVisibleMonth((currentMonth) =>
                    startOfMonth(addMonths(currentMonth, 1)),
                  )
                }
                sx={{
                  color: "#2A252B",
                  width: 42,
                  height: 42,
                }}
              >
                <ChevronRightRounded />
              </IconButton>
            </Box>

            {isLoading ? (
              <Box
                sx={{
                  minHeight: 380,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ color: "text.secondary", fontWeight: 500 }}
                >
                  Loading reproductive health data...
                </Typography>
              </Box>
            ) : hasCalendarData ? (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
                  gap: { xs: 3.25, md: 4.5 },
                }}
              >
                {calendarMonths.map(({ month, days }) => (
                  <Box key={month.toISOString()}>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
                        columnGap: { xs: 0.5, md: 0.85 },
                        rowGap: { xs: 0.8, md: 1.1 },
                      }}
                    >
                      {DAY_LABELS.map((label) => (
                        <Typography
                          key={`${month.toISOString()}-${label}`}
                          sx={{
                            textAlign: "center",
                            color: "#948F9C",
                            fontSize: { xs: "1.15rem", md: "1.3rem" },
                            fontWeight: 500,
                            py: 0.4,
                          }}
                        >
                          {label}
                        </Typography>
                      ))}

                      {days.map((day) => (
                        <Box
                          key={day.date.toISOString()}
                          sx={{
                            minHeight: { xs: 44, md: 60 },
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Box
                            sx={{
                              position: "relative",
                              width: { xs: 39, md: 47 },
                              height: { xs: 39, md: 47 },
                              borderRadius: "999px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              backgroundColor: day.isPeriod
                                ? "#F2CDCF"
                                : "transparent",
                              color: day.inCurrentMonth ? "#2E2930" : "#C7C0CB",
                              fontSize: { xs: "1.2rem", md: "1.32rem" },
                              fontWeight: 500,
                            }}
                          >
                            {format(day.date, "d")}

                            {day.isSpotting ? (
                              <Box
                                sx={{
                                  position: "absolute",
                                  left: "50%",
                                  bottom: -2,
                                  transform: "translateX(-50%)",
                                  width: 12,
                                  height: 12,
                                  borderRadius: "50%",
                                  backgroundColor: "#7075F0",
                                  boxShadow: "0 0 0 3px #FFFFFF",
                                }}
                              />
                            ) : null}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : (
              <Box
                sx={{
                  minHeight: 380,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 2,
                }}
              >
                <FemaleIcon
                  sx={{
                    color: "text.disabled",
                    fontSize: 48,
                    opacity: 0.5,
                  }}
                />
                <Typography
                  variant="h6"
                  sx={{
                    color: "text.secondary",
                    fontWeight: 500,
                  }}
                >
                  No reproductive health data available
                </Typography>
              </Box>
            )}
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1.3,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
              <Box
                sx={{
                  width: 18,
                  height: 18,
                  borderRadius: "50%",
                  backgroundColor: "#F2CDCF",
                }}
              />
              <Typography
                variant="body1"
                sx={{ color: "#2D2830", fontWeight: 600, fontSize: "1.12rem" }}
              >
                Period detected
              </Typography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  borderRadius: "50%",
                  backgroundColor: "#7075F0",
                }}
              />
              <Typography
                variant="body1"
                sx={{ color: "#2D2830", fontWeight: 600, fontSize: "1.12rem" }}
              >
                Spotting detected
              </Typography>
            </Box>
          </Box>
        </Box>
      </PatientDetailPageContainer>
    </DashboardLayout>
  );
};

export default ReproductiveHealthPage;
